import React, { useEffect } from 'react';
import { Check, Stethoscope } from 'lucide-react'; // Keep Check & fallback
import useSpecialtiesStore from '../../store/specialtiesStore';
import useRegistrationStore from '../../store/useRegistrationStore';

// Import all specialty images
import anesthesia from '../../assets/anesthesia.png';
import cardiology from '../../assets/cardiology.png';
import cardiothorasic_surgery from '../../assets/cardiothorasic_surgery.png';
import cardiovascular from '../../assets/cardiovascular.png';
import dental from '../../assets/dental.png';
import dermatology from '../../assets/dermatology.png';
import endocrinology from '../../assets/endocrinology.png';
import ent from '../../assets/ent.png';
import gastroentrology from '../../assets/gastroentrology.png';
import general_doctor from '../../assets/general_doctor.png';
import general_surgery from '../../assets/general_surgery.png';
import heamatology from '../../assets/heamatology.png';
import immunology from '../../assets/immunology.png';
import infected_disease from '../../assets/infected_disease.png';
import internal_medicine from '../../assets/internal_medicine.png';
import microbiology from '../../assets/microbiology.png';
import neurology from '../../assets/neurology.png';
import neurovascular_surgery from '../../assets/neurovascular_surgery.png';
import obstetrics from '../../assets/obstetrics.png';
import opthalmology from '../../assets/opthalmology.png';
import facial from '../../assets/facial.png'; // Oral & Maxillofacial
import ortopedic from '../../assets/ortopedic.png'; // Orthopaedic
import paediatric from '../../assets/paediatric.png';
import physiotherapy from '../../assets/physiotherapy.png';
import psychiatry from '../../assets/psychiatry.png';
import rehabilitation from '../../assets/rehabilitation.png';
import rheumatology from '../../assets/rheumatology.png';
import sports_medicine from '../../assets/sports_medicine.png';
import urology from '../../assets/urology.png';
import vascular_surgery from '../../assets/vascular_surgery.png';

const MedicalSpecialitiesStep = () => {
    // Zustand stores
    const { specialties, fetchSpecialties, loading, error } = useSpecialtiesStore();
    const { selectedSpecialities, toggleSpeciality } = useRegistrationStore();

    // Fetch specialties on mount
    useEffect(() => {
        fetchSpecialties();
    }, [fetchSpecialties]);



    // Map specialty ID → imported image
    const getSpecialityImage = (id) => {
        const imageMap = {
            1: anesthesia,          // Anesthesia
            2: cardiology,          // Cardiology
            3: cardiothorasic_surgery, // Cardiothorasic Surgery
            4: cardiovascular,      // Cardiovascular intervention
            5: dental,              // Dental
            6: dermatology,         // Dermatology
            7: endocrinology,       // Endocrinology
            8: ent,                 // ENT
            9: gastroentrology,     // Gastroentrology
            10: internal_medicine,  // Internal Medicine
            11: general_surgery,    // General Surgery
            12: general_doctor,     // General Practitioner
            13: heamatology,        // Heamatology
            14: immunology,         // Immunology
            15: infected_disease,   // Infectious Disease
            16: microbiology,       // Microbiology & Virology
            17: neurology,          // Neurology
            18: neurovascular_surgery, // Neurovascular Surgery
            19: obstetrics,         // Obstetric & Gynaecology
            20: opthalmology,       // Opthalmology
            21: facial,             // Oral & Maxilo facial surgery
            22: ortopedic,          // Orthopaedic
            23: paediatric,         // Paediatric
            24: physiotherapy,      // Physiotherapy
            25: psychiatry,         // Psychiatry
            26: rehabilitation,     // Rehabilitation Medicine
            27: rheumatology,       // Rheumatology
            28: sports_medicine,    // Sports medicine
            29: urology,            // Urology
            30: vascular_surgery,   // Vascular surgery
        };
        return imageMap[id] || null; // null means fallback to icon
    };

    // Convert array to Set for checking
    const selectedSet = new Set(selectedSpecialities);

    if (loading) return <p className="text-center text-gray-600">Loading specialties...</p>;
    if (error) return <p className="text-center text-red-500">{error}</p>;

    return (
        <div className="space-y-8 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Stethoscope className="w-6 h-6 text-primary" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Medical Specialities</h2>
                    <p className="text-base text-gray-600 mt-1">
                        Select all specialties available at your facility
                    </p>
                </div>
            </div>

            {/* Specialties Grid */}
            <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 md:p-8 shadow-xl">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {specialties.map((speciality) => {
                        const id = speciality.MedicalSpecialities_ID;
                        const isSelected = selectedSet.has(id);
                        const imageSrc = getSpecialityImage(id);

                        return (
                            <button
                                key={id}
                                onClick={() => toggleSpeciality(id)}
                                className={`
                  relative flex flex-col items-center justify-center p-4 min-h-[130px]
                  bg-white border-2 rounded-xl transition-all duration-200 shadow-sm
                  hover:shadow-md hover:border-primary/50
                  ${isSelected ? 'border-primary bg-primary/5 shadow-primary/20' : 'border-gray-200'}
                `}
                            >
                                <div className={`
                  w-16 h-16 rounded-xl flex items-center justify-center mb-3 transition-transform overflow-hidden
                  ${isSelected ? 'bg-primary/10 scale-105' : 'bg-gray-100'}
                `}>
                                    {imageSrc ? (
                                        <img
                                            src={imageSrc}
                                            alt={speciality.SpecialitiesName}
                                            className="w-full h-full object-contain"
                                        />
                                    ) : (
                                        <Stethoscope className={`w-8 h-8 ${isSelected ? 'text-primary' : 'text-gray-600'}`} />
                                    )}
                                </div>
                                <p className={`
                  text-xs sm:text-sm font-medium leading-tight text-center px-1
                  ${isSelected ? 'text-primary' : 'text-gray-700'}
                `}>{speciality.SpecialitiesName}</p>

                                {isSelected && (
                                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow-md">
                                        <Check className="w-4 h-4 text-white" strokeWidth={3} />
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>

            </div>

            {/* Selection Summary */}
            <div className="bg-white border-2 border-gray-100 rounded-2xl p-6 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-md">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                        <Check className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-center sm:text-left">
                        <p className="text-sm text-gray-600">Selected specialties</p>
                        <p className="text-2xl font-bold text-gray-900">{selectedSpecialities.length}</p>
                    </div>
                </div>
                <p className="text-sm text-gray-600">
                    Out of <span className="font-semibold text-gray-800">{specialties.length}</span> available
                </p>
            </div>
        </div>
    );
};

export default MedicalSpecialitiesStep;